import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Building2, Calendar, Users, DollarSign, MapPin } from "lucide-react";
import { PurchaseOrderData, PurchaseOrderValidation } from "@/components/campaign/types/purchaseOrderV2";
import { formatAmount } from "@/components/campaign/utils/purchaseOrderUtils";
import { format } from "date-fns";

interface ReviewStepProps {
  purchaseOrderData: PurchaseOrderData;
  validation: PurchaseOrderValidation;
  onUpdate: (updates: Partial<PurchaseOrderData>) => void;
}

export const ReviewStep = ({ purchaseOrderData }: ReviewStepProps) => {
  const { companyDetails, devices, duration, amount, selectedLocations, budgetCalculations } = purchaseOrderData;
  const totalAmount = budgetCalculations?.totalAmount ?? (budgetCalculations as any)?.totalBudget ?? amount;
  
  // Get payment plan from localStorage
  const paymentPlanStr = localStorage.getItem('selectedPaymentPlan');
  const paymentPlan = paymentPlanStr ? JSON.parse(paymentPlanStr) : null;
  
  // Calculate display amount based on payment plan
  const isInstallments = paymentPlan?.type === 'installments';
  const installmentCount = paymentPlan?.installmentCount || 1;
  const displayAmount = isInstallments ? totalAmount / installmentCount : totalAmount;
  const amountLabel = isInstallments ? `First Installment (1 of ${installmentCount})` : 'Total Amount';
  
  // Generate installment schedule (30, 60, 90 days)
  const getInstallmentSchedule = () => {
    if (!isInstallments) return [];
    return Array.from({ length: installmentCount }, (_, i) => ({
      number: i + 1,
      dueInDays: (i + 1) * 30,
      amount: displayAmount
    }));
  };
  
  const installmentSchedule = getInstallmentSchedule();
  
  return (
    <div className="space-y-6">
      {/* Payment Plan Notice */}
      {paymentPlan && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Payment Plan Selected</h4>
                <p className="text-sm text-muted-foreground">
                  {isInstallments ? (
                    <>
                      <strong>{installmentCount} Installments</strong> - {formatAmount(displayAmount)} SAR each
                      <br />
                      <span className="text-xs mt-2 block">Payment schedule:</span>
                      <span className="text-xs block">
                        {installmentSchedule.map((inst, idx) => (
                          <span key={inst.number}>
                            • Installment {inst.number}: Due after {inst.dueInDays} days
                            {idx < installmentSchedule.length - 1 && <br />}
                          </span>
                        ))}
                      </span>
                      <span className="text-xs block mt-2">Total campaign budget: {formatAmount(totalAmount)} SAR</span>
                    </>
                  ) : (
                    <>
                      <strong>Full Payment</strong> - Pay the complete amount within {paymentPlan.dueDays} days.
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>Company Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Company Name</label>
              <p className="font-medium">{companyDetails.companyName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Brand Name</label>
              <p className="font-medium">{companyDetails.brandName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Person in Charge</label>
              <p className="font-medium">{companyDetails.personInCharge}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Job Title</label>
              <p className="font-medium">{companyDetails.jobTitle}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="font-medium">{companyDetails.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Campaign Details</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Devices</p>
                <p className="text-2xl font-bold">{devices}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="text-2xl font-bold">{duration} days</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{amountLabel}</p>
                <p className="text-2xl font-bold">SAR {formatAmount(displayAmount)}</p>
                {isInstallments && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Campaign total: {formatAmount(totalAmount)} SAR
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Locations */}
      {selectedLocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Selected Locations ({selectedLocations.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {selectedLocations.slice(0, 5).map((location, index) => (
                <div key={location.id || index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{location.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {location.city && `${location.city} • `}
                      {location.mediaOwner}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {location.devices} screens
                  </Badge>
                </div>
              ))}
              {selectedLocations.length > 5 && (
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground">
                    ... and {selectedLocations.length - 5} more locations
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Breakdown */}
      {budgetCalculations && (
        <Card>
          <CardHeader>
            <CardTitle>Budget Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span>Base Total</span>
              <span>SAR {formatAmount(budgetCalculations.baseTotal)}</span>
            </div>
            
            {budgetCalculations.discounts?.map((discount, index) => (
              <div key={index} className="flex justify-between text-green-600">
                <span>{discount.mediaOwner} Discount ({(discount.discountRate * 100).toFixed(0)}%)</span>
                <span>-SAR {formatAmount(discount.discountAmount)}</span>
              </div>
            ))}
            
            <Separator />
            
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>SAR {formatAmount(budgetCalculations.subtotal)}</span>
            </div>
            
            <div className="flex justify-between">
              <span>VAT (15%)</span>
              <span>SAR {formatAmount(budgetCalculations.vatAmount)}</span>
            </div>
            
            <Separator />
            
            <div className="flex justify-between text-lg font-bold">
              <span>Campaign Total</span>
              <span>SAR {formatAmount(totalAmount)}</span>
            </div>
            
            {isInstallments && (
              <>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-primary">
                  <span>First Installment Due (in 30 Days)</span>
                  <span>SAR {formatAmount(displayAmount)}</span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Remaining installments:</p>
                  {installmentSchedule.slice(1).map((inst) => (
                    <p key={inst.number}>
                      • Installment {inst.number}: {formatAmount(inst.amount)} SAR (due after {inst.dueInDays} days)
                    </p>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Purchase Order Information */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Order Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">PO Number</label>
              <p className="font-mono text-lg">{purchaseOrderData.poNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Date Created</label>
              <p>{format(new Date(purchaseOrderData.createdAt), 'PPP')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};